import { useState, useMemo } from 'react';
import { useProjectContext } from '../contexts/ProjectContext';
import { MutuoForm } from '../components/Finanziamento/MutuoForm';
import { PrestitoForm } from '../components/Finanziamento/PrestitoForm';
import { FondiPropriForm } from '../components/Finanziamento/FondiPropriForm';
import { LiquiditaSummary } from '../components/Finanziamento/LiquiditaSummary';
import { FinanziabilitaWizard } from '../components/Finanziamento/FinanziabilitaWizard';
import {
  calculateFinancingSummary,
  formatRate,
  calcolaFinanziabilita,
} from '../services/financeCalculations';
import { calculateDashboardTotals } from '../services/calculations';
import { formatCurrency, parseDecimal } from '../utils/pricing';
import {
  useCreateFinanziamento,
  useUpdateFinanziamento,
  useDeleteFinanziamento,
} from '../hooks/useFinanziamenti';
import type { Finanziamento, EstimateLevel } from '../types';

type FormType = 'mutuo' | 'prestito' | 'fondi_propri' | null;

export function FinanziamentoPage() {
  const { currentProject } = useProjectContext();
  const [estimateLevel] = useState<EstimateLevel>('med');

  const createMutation = useCreateFinanziamento();
  const updateMutation = useUpdateFinanziamento();
  const deleteMutation = useDeleteFinanziamento();

  const isSaving =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const [showForm, setShowForm] = useState<FormType>(null);
  const [editingFinanziamento, setEditingFinanziamento] = useState<Finanziamento | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  const summary = useMemo(
    () => calculateFinancingSummary(currentProject?.finanziamenti || []),
    [currentProject?.finanziamenti]
  );

  const dashboardTotals = useMemo(
    () => calculateDashboardTotals(currentProject, estimateLevel),
    [currentProject, estimateLevel]
  );

  // Compute semaphore from saved profile
  const semaforoResult = useMemo(() => {
    const profilo = currentProject?.profiloFinanziario;
    if (!profilo || !currentProject?.finanziamenti) return null;
    return calcolaFinanziabilita(profilo, currentProject.finanziamenti);
  }, [currentProject?.profiloFinanziario, currentProject?.finanziamenti]);

  const handleSave = async (data: Partial<Finanziamento>) => {
    if (!currentProject) return;
    try {
      if (editingFinanziamento) {
        await updateMutation.mutateAsync({
          id: editingFinanziamento.id,
          projectId: currentProject.id,
          data,
        });
        setEditingFinanziamento(null);
      } else {
        await createMutation.mutateAsync({
          projectId: currentProject.id,
          tipo: data.tipo!,
          nome: data.nome!,
          importo: data.importo!,
          tassoInteresse: data.tassoInteresse || '0.0000',
          durataAnni: data.durataAnni ?? 0,
          rataMensile: data.rataMensile ?? undefined,
          dataErogazione: data.dataErogazione ?? undefined,
          dataPrimaRata: data.dataPrimaRata ?? undefined,
          note: data.note ?? undefined,
        });
      }
      setShowForm(null);
    } catch {
      // Error handled by React Query
    }
  };

  const handleDelete = async (id: string) => {
    if (!currentProject) return;
    if (window.confirm('Sei sicuro di voler eliminare questo finanziamento?')) {
      try {
        await deleteMutation.mutateAsync({
          id,
          projectId: currentProject.id,
        });
      } catch {
        // Error handled by React Query
      }
    }
  };

  const handleEdit = (finanziamento: Finanziamento) => {
    setEditingFinanziamento(finanziamento);
    setShowForm(finanziamento.tipo);
  };

  const handleCancel = () => {
    setShowForm(null);
    setEditingFinanziamento(null);
  };

  if (!currentProject) {
    return (
      <div className="text-center py-12">
        <h2 className="text-lg font-semibold text-gray-900">
          Nessun progetto selezionato
        </h2>
        <p className="mt-2 text-gray-600">
          Seleziona un progetto per gestire i finanziamenti.
        </p>
      </div>
    );
  }

  const semaforoBadgeColors = {
    verde: 'bg-success-100 text-success-700 border-success-300',
    giallo: 'bg-warning-100 text-warning-700 border-warning-300',
    rosso: 'bg-danger-100 text-danger-700 border-danger-300',
  };

  const semaforoBadgeLabels = {
    verde: 'Finanziabile',
    giallo: 'Attenzione',
    rosso: 'Critico',
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Finanziamenti</h1>
            <p className="mt-1 text-sm text-gray-600">
              Configura mutui, prestiti e fondi propri per {currentProject.name}
            </p>
          </div>
          {semaforoResult && (
            <span
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border ${
                semaforoBadgeColors[semaforoResult.esito]
              }`}
            >
              <span
                className={`w-2 h-2 rounded-full ${
                  semaforoResult.esito === 'verde'
                    ? 'bg-success-500'
                    : semaforoResult.esito === 'giallo'
                      ? 'bg-warning-500'
                      : 'bg-danger-500'
                }`}
              />
              {semaforoBadgeLabels[semaforoResult.esito]}
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowWizard(true)}
            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
              semaforoResult
                ? `${semaforoBadgeColors[semaforoResult.esito]} hover:opacity-80`
                : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
            }`}
          >
            <svg
              className="w-4 h-4 mr-1.5 inline-block"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            Valuta Finanziabilita
          </button>
          <button
            onClick={() => setShowForm('mutuo')}
            className="btn-primary"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Mutuo
          </button>
          <button
            onClick={() => setShowForm('prestito')}
            className="btn-secondary"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Prestito
          </button>
          <button
            onClick={() => setShowForm('fondi_propri')}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-100 text-amber-800 border border-amber-300 hover:bg-amber-200 transition-colors inline-flex items-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Fondi Propri
          </button>
        </div>
      </div>

      {/* Liquidity Summary */}
      <LiquiditaSummary
        summary={summary}
        totalCosts={dashboardTotals.totaleComplessivo}
      />

      {/* Finanziamenti List */}
      {(currentProject.finanziamenti?.length || 0) > 0 && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Finanziamenti Attivi
          </h3>
          <div className="space-y-4">
            {currentProject.finanziamenti?.map((f: Finanziamento) => (
              <div
                key={f.id}
                className={`p-4 rounded-lg border ${
                  f.tipo === 'mutuo'
                    ? 'bg-primary-50 border-primary-200'
                    : f.tipo === 'fondi_propri'
                      ? 'bg-amber-50 border-amber-200'
                      : 'bg-success-50 border-success-200'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        className={`badge ${
                          f.tipo === 'mutuo'
                            ? 'badge-primary'
                            : f.tipo === 'fondi_propri'
                              ? 'bg-amber-100 text-amber-800'
                              : 'badge-success'
                        }`}
                      >
                        {f.tipo === 'mutuo'
                          ? 'Mutuo'
                          : f.tipo === 'fondi_propri'
                            ? 'Fondi Propri'
                            : 'Prestito'}
                      </span>
                      <h4 className="font-medium text-gray-900">{f.nome}</h4>
                    </div>
                    {f.tipo === 'fondi_propri' ? (
                      <div className="mt-3 text-sm">
                        <div>
                          <p className="text-gray-500">Importo</p>
                          <p className="font-semibold text-lg currency">
                            {formatCurrency(parseDecimal(f.importo))}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-500">Importo</p>
                          <p className="font-semibold currency">
                            {formatCurrency(parseDecimal(f.importo))}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Tasso</p>
                          <p className="font-semibold">
                            {formatRate(parseDecimal(f.tassoInteresse))}
                          </p>
                        </div>
                        <div>
                          <p className="text-gray-500">Durata</p>
                          <p className="font-semibold">{f.durataAnni} anni</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Rata mensile</p>
                          <p className="font-semibold currency">
                            {formatCurrency(parseDecimal(f.rataMensile))}
                          </p>
                        </div>
                      </div>
                    )}
                    {f.note && (
                      <p className="mt-2 text-sm text-gray-600">{f.note}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 ml-4">
                    <button
                      onClick={() => handleEdit(f)}
                      className="p-2 text-gray-400 hover:text-primary-600 hover:bg-white rounded-lg transition-colors"
                      title="Modifica"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="p-2 text-gray-400 hover:text-danger-600 hover:bg-white rounded-lg transition-colors"
                      title="Elimina"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {(currentProject.finanziamenti?.length || 0) === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Nessun finanziamento
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            Aggiungi un mutuo, un prestito o fondi propri per iniziare.
          </p>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto animate-slide-up">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-gray-900">
                  {editingFinanziamento ? 'Modifica' : 'Nuovo'}{' '}
                  {showForm === 'mutuo'
                    ? 'Mutuo'
                    : showForm === 'fondi_propri'
                      ? 'Fondi Propri'
                      : 'Prestito'}
                </h2>
                <button
                  onClick={handleCancel}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {showForm === 'mutuo' ? (
                <MutuoForm
                  finanziamento={editingFinanziamento || undefined}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isLoading={isSaving}
                />
              ) : showForm === 'fondi_propri' ? (
                <FondiPropriForm
                  finanziamento={editingFinanziamento || undefined}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isLoading={isSaving}
                />
              ) : (
                <PrestitoForm
                  finanziamento={editingFinanziamento || undefined}
                  onSave={handleSave}
                  onCancel={handleCancel}
                  isLoading={isSaving}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Finanziabilita Wizard */}
      {showWizard && (
        <FinanziabilitaWizard
          onClose={() => setShowWizard(false)}
          existingProfilo={currentProject.profiloFinanziario}
        />
      )}
    </div>
  );
}
